import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;

  if (!video) {
    return (
      <>
        <SEOHead
          title="视频未找到 - 国产精品视频精选推荐平台"
          description="抱歉，您访问的视频内容不存在。"
          keywords="国产精品视频"
          canonicalPath={`/article/${id || ''}`}
        />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-4">视频未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = getRelatedVideos(video);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.publishDate,
    author: {
      '@type': 'Organization',
      name: '国产精品视频精选推荐平台',
    },
    image: video.thumbnail,
    publisher: {
      '@type': 'Organization',
      name: '国产精品视频精选推荐平台',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${video.title} - 国产精品${video.categoryName}视频在线观看`}
        description={video.description}
        keywords={`国产精品视频,国产${video.categoryName}视频,${video.title},${video.tags.join(',')}`}
        canonicalPath={`/article/${video.id}`}
        ogType="article"
        ogImage={video.thumbnail}
        schemaJson={[articleSchema]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: video.categoryName, path: `/category/${video.category}` },
                { label: video.title },
              ]}
            />

            <article className="mt-6">
              <div className="relative rounded-lg overflow-hidden aspect-video mb-8">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-colors cursor-pointer">
                    <i className="ri-play-fill text-3xl md:text-4xl text-background-50 ml-1"></i>
                  </div>
                </div>
                <span className="absolute bottom-3 right-3 bg-black/70 text-background-50 text-sm px-3 py-1 rounded-md whitespace-nowrap">
                  {video.duration}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                  {video.categoryName}
                </span>
                <div className="flex items-center gap-1 text-accent-500">
                  <i className="ri-star-fill"></i>
                  <span className="text-sm font-semibold text-foreground-900">{video.rating}</span>
                </div>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatCount(video.viewCount)} 次播放
                </span>
                <span className="text-sm text-foreground-500">{video.publishDate}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                {video.title}
              </h1>

              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-8">
                <p>{video.description}</p>
                <p className="mt-4">
                  本视频由国产精品视频精选推荐平台精心筛选推荐，致力于为用户提供高品质的国产原创视频内容。无论是画面质量还是内容深度，都经过了严格的审核和筛选，确保每一位观众都能获得优质的观看体验。
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-12">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </article>

            {relatedVideos.length > 0 && (
              <section className="border-t border-background-200 pt-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} variant="compact" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}

function formatCount(count: number): string {
  if (count >= 10000) {
    return (count / 10000).toFixed(1) + '万';
  }
  return count.toString();
}